import { createRoot } from "react-dom/client";
import Modal from "react-modal";
import { ErrorBoundary } from "@sentry/react";
import { useFlagsStatus } from "@unleash/proxy-client-react";
import { FC, ReactElement, ReactNode, StrictMode, Suspense } from "react";
import { FeatureFlagsProvider } from "@topi/shared/platform/feature-flags/feature-flags-provider";
import { LoadingLayout } from "@topi/ui/layouts/loading-layout/loading-layout";
import { ErrorPage } from "@topi/shared/common/features/error-page/error-page";
import { AnalyticsProvider } from "@topi/shared/platform/analytics/analytics";
import { GlobalStyle } from "@topi/ui/styles/global-style/global-style";
import { initI18n } from "@topi/shared/platform/i18n/config";
import { useInitSentry } from "@topi/shared/hooks/use-init-sentry";
import { useRefreshAPILanguage } from "@topi/shared/hooks/use-refresh-api-language";

const AppShell: FC<{ children: ReactNode }> = ({ children }) => {
  initI18n();
  useInitSentry();
  useRefreshAPILanguage();
  const { flagsReady } = useFlagsStatus();
  if (!flagsReady) {
    return <LoadingLayout />;
  }
  return <Suspense fallback={<LoadingLayout />}>{children}</Suspense>;
};

export const bootstrap = (app: ReactElement, appName: string): void => {
  document.body.append(
    Object.assign(document.createElement(`div`), { id: "root" })
  );

  Modal.setAppElement("#root");

  const container = document.querySelector("#root");

  if (container === null) {
    throw new Error("root container is null");
  }

  const root = createRoot(container);
  root.render(
    <StrictMode>
      <ErrorBoundary fallback={<ErrorPage />}>
        <GlobalStyle />
        {/* The LoadingLayout depends on localized strings, so we need a fallback for that */}
        <Suspense fallback={<></>}>
          <Suspense fallback={<LoadingLayout />}>
            <FeatureFlagsProvider
              url={import.meta.env.UNLEASH_PROXY_URL}
              proxyKey={import.meta.env.UNLEASH_PROXY_KEY}
              currentApp={appName}
            >
              <AnalyticsProvider writeKey={import.meta.env.SEGMENT_WRITE_KEY}>
                <AppShell>{app}</AppShell>
              </AnalyticsProvider>
            </FeatureFlagsProvider>
          </Suspense>
        </Suspense>
      </ErrorBoundary>
    </StrictMode>
  );
};
